import React from 'react';
import { ApiRequest, API_AUTH_TYPES, API_ROUTES } from '../api';
import { getStoredHouseholdId } from '../helpers/authStorage';
import { handleBoundaryError } from '../utils/globalErrorHandler';

const isChunkLoadError = (error) => {
    const name = error?.name || '';
    const msg = (error?.message || '').toLowerCase();
    if (name === 'ChunkLoadError') return true;
    if (msg.includes('loading chunk') && msg.includes('failed')) return true;
    if (msg.includes("unexpected token '<'")) return true;
    return false;
};

/**
 * Error Boundary Component — catches React component errors and logs them to the server.
 * Renders a user-friendly fallback UI when any child component throws during rendering.
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
        this.errorLoggingApi = new ApiRequest(
            API_ROUTES.ERROR_LOGGING,
            API_AUTH_TYPES.USER
        );
    }

    /**
     * React lifecycle: updates state so the next render shows the fallback UI.
     * @param {Error} error - The error that was thrown.
     * @returns {{ hasError: boolean }}
     */
    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    /**
     * React lifecycle: called after an error has been thrown by a descendant component.
     * Stores the error details in state and triggers server-side logging.
     * @param {Error} error - The error that was thrown.
     * @param {React.ErrorInfo} errorInfo - Object containing the component stack trace.
     */
    componentDidCatch(error, errorInfo) {
        // Chunk-load errors after a deploy: trigger reload-once recovery
        // instead of logging + showing the generic fallback. handleBoundaryError
        // detects chunk errors and short-circuits to handleChunkLoadError.
        if (isChunkLoadError(error)) {
            handleBoundaryError(error, errorInfo);
            return;
        }

        this.setState({ error, errorInfo });

        // Log the error to the server
        this.logErrorToServer(error, errorInfo);
    }

    /**
     * Posts the caught error details to the server-side error logging endpoint.
     * Silently swallows logging failures to avoid cascading errors.
     * @param {Error} error - The original thrown error.
     * @param {React.ErrorInfo} errorInfo - React's component stack information.
     * @returns {Promise<void>}
     */
    async logErrorToServer(error, errorInfo) {
        try {
            if (!localStorage.getItem('access_token')) {
                console.error('[ERROR-BOUNDARY] No access token; skipping server log:', error);
                return;
            }

            const household_id = getStoredHouseholdId();
            
            await this.errorLoggingApi.post({
                endpoint: 'log_error',
                body: {
                    household_id,
                    error_type: 'component_error',
                    error_message: error.toString(),
                    error_stack: error.stack || errorInfo.componentStack,
                    url: window.location.href,
                    component_name: errorInfo.componentStack?.split('\n')[1]?.trim() || 'Unknown',
                    user_action: 'Component rendered',
                    browser_info: {
                        userAgent: navigator.userAgent,
                        language: navigator.language,
                        platform: navigator.platform,
                        viewport: {
                            width: window.innerWidth,
                            height: window.innerHeight
                        }
                    },
                    metadata: {
                        componentStack: errorInfo.componentStack
                    }
                }
            });
        } catch (loggingError) {
            console.error('[ERROR-BOUNDARY] Failed to log error:', loggingError);
        }
    }

    /**
     * Renders the fallback error UI when an error has been caught, otherwise renders children.
     * @returns {JSX.Element}
     */
    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
                        <div className="flex items-center mb-4">
                            <svg
                                className="h-6 w-6 text-red-500 mr-2"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <h1 className="text-xl font-semibold text-gray-900">
                                Something went wrong
                            </h1>
                        </div>
                        <p className="text-gray-600 mb-4">
                            We're sorry, but something unexpected happened. Our team has been notified and is working to fix the issue.
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                            className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors"
                        >
                            Reload Page
                        </button>
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details className="mt-4">
                                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                                    Error Details (Development Only)
                                </summary>
                                <div className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto">
                                    <p className="font-semibold mb-1">{this.state.error.toString()}</p>
                                    <pre className="whitespace-pre-wrap">{this.state.errorInfo?.componentStack}</pre>
                                </div>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;


